import React from 'react';
import { Link } from 'react-router-dom';
import { changelogUpdates } from '../data/changelogData';
import HomeAlertModal from '../components/HomeAlertModal';
import './Home.css';

function Home() {
  const latestUpdates = changelogUpdates.slice(0, 3);

  return (
    <div className="home-container">
      <HomeAlertModal />
      {/* Animated Background Elements */}
      <div className="bg-animations">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
      </div>

      {/* --- HERO SECTION --- */}
      <section className="hero-section container">
        <div className="hero-content animate-up">
          <div className="hero-badge">
            <span className="live-dot"></span> Live Developer Dashboard
          </div>
          <h1 className="hero-title">
            Neural Developer Analytics in <span className="text-gradient">Real Time</span>
          </h1>
          <p className="hero-subtitle">
            A unified dashboard that visualizes your competitive programming progress and open-source contributions across the most popular developer platforms.
          </p>
          <div className="hero-buttons">
            <Link to="/register" className="btn btn-primary btn-glow">Create Your Profile</Link>
            <Link to="/dev/demo" className="btn btn-secondary">View Example Dashboard</Link>
          </div>
          <div className="hero-platforms">
            <div className="platform-icon github"><i className="fa-brands fa-github"></i></div>
            <div className="platform-icon leetcode"><i className="fa-solid fa-code"></i></div>
            <div className="platform-icon codeforces"><i className="fa-solid fa-chart-simple"></i></div>
            <div className="platform-icon gfg"><i className="fa-solid fa-laptop-code"></i></div>
          </div>
        </div>

        {/* 3D Dashboard Mockup */}
        <div className="hero-mockup animate-up" style={{ animationDelay: '0.3s' }}>
          <div className="mockup-window glass-card">
            <div className="mockup-header">
              <span className="dot dot-r"></span>
              <span className="dot dot-y"></span>
              <span className="dot dot-g"></span>
            </div>
            <div className="mockup-body">
              <div className="mockup-sidebar"></div>
              <div className="mockup-main">
                <div className="mockup-stats">
                  <div className="mockup-stat-card github"><i className="fa-brands fa-github"></i></div>
                  <div className="mockup-stat-card leetcode"><i className="fa-solid fa-code"></i></div>
                </div>
                <div className="mockup-chart"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- WHAT'S NEW SECTION --- */}
      <section className="whats-new-section container animate-up">
        <div className="section-header">
          <h2 className="section-title">What's New</h2>
          <p className="section-subtitle">The latest nodes in the DevTracker evolution.</p>
        </div>
        <div className="whats-new-grid">
          {latestUpdates.map((update, idx) => (
            <Link key={update.version} to={`/whats-new/${update.version}`} className="whats-new-card glass-card">
              <div className="update-status">
                <span className="version-tag">{update.version}</span>
                <span className={`status-pill ${update.type || 'feature'}`}>{update.type || 'Update'}</span>
              </div>
              <h3>{update.title}</h3>
              <p>{update.description}</p>
              <div className="card-footer">
                <span>{update.date}</span>
                <i className="fa-solid fa-arrow-right-long"></i>
              </div>
            </Link>
          ))}
        </div>
        <div className="whats-new-cta">
          <Link to="/changelog" className="text-link">Explore Full Platform Chronology <i className="fa-solid fa-chevron-right"></i></Link>
        </div>
      </section>

      {/* --- FEATURES SECTION --- */}
      <section className="features-section container">
        <div className="section-header">
          <h2 className="section-title">Why Developers Love DevTracker</h2>
          <div className="section-line"></div>
        </div>
        <div className="features-grid">
          <div className="feature-card glass-card">
            <div className="feature-icon"><i className="fa-solid fa-bolt"></i></div>
            <h3>Live Coding Stats</h3>
            <p>Automatically fetch GitHub, LeetCode, Codeforces, and GeeksforGeeks statistics without any manual entry.</p>
          </div>
          <div className="feature-card glass-card">
            <div className="feature-icon"><i className="fa-solid fa-wand-magic-sparkles" style={{color: 'var(--brand-accent)'}}></i></div>
            <h3>Simplified Intelligence</h3>
            <p>Leverage the <strong>Origin Core</strong> to generate DNA scores and career personas based on your verified platform statistics.</p>
          </div>
          <div className="feature-card glass-card">
            <div className="feature-icon"><i className="fa-solid fa-code"></i></div>
            <h3>Embeddable Dashboard</h3>
            <p>Add your live coding profile widget to any website, blog, or Notion page with ultra-low latency.</p>
          </div>
          <div className="feature-card glass-card">
            <div className="feature-icon"><i className="fa-solid fa-share-nodes"></i></div>
            <h3>Shareable Portfolios</h3>
            <p>Professional public profiles designed to showcase your cross-platform technical blueprint to recruiters.</p>
          </div>
          <div className="feature-card glass-card">
            <div className="feature-icon"><i className="fa-solid fa-bolt"></i></div>
            <h3>Real-Time Sync</h3>
            <p>Automatic background synchronization ensures your stats are never stale, across all major dev platforms.</p>
          </div>
        </div>
      </section>

      {/* --- HOW IT WORKS SECTION --- */}
      <section className="how-section container">
        <div className="section-header">
          <h2 className="section-title">How DevTracker Works</h2>
          <div className="section-line"></div>
        </div>
        <div className="how-grid">
          <div className="how-step glass-card">
            <div className="step-number">1</div>
            <h3>Create Your Account</h3>
            <p>Sign up and generate your unique developer profile ID instantly.</p>
          </div>
          <div className="how-step glass-card">
            <div className="step-number">2</div>
            <h3>Add Usernames</h3>
            <p>Connect your GitHub, LeetCode, Codeforces, and GFG handles.</p>
          </div>
          <div className="how-step glass-card">
            <div className="step-number">3</div>
            <h3>Share Dashboard</h3>
            <p>Your coding stats update automatically. Embed or share anywhere!</p>
          </div>
        </div>
      </section>

      {/* --- DASHBOARD PREVIEW SECTION --- */}
      <section className="preview-section container">
        <div className="section-header">
          <h2 className="section-title">Your Stats, Visualized</h2>
          <p className="section-subtitle">A sneak peek at the real-time stat cards on your future dashboard.</p>
        </div>
        <div className="preview-grid">
          {/* GitHub Mock */}
          <div className="glass-card stat-preview-card github">
            <div className="stat-preview-header">
              <i className="fa-brands fa-github"></i>
              <span>GitHub</span>
            </div>
            <div className="stat-preview-body">
              <div className="stat-row"><span>Repositories</span><strong>42</strong></div>
              <div className="stat-row"><span>Followers</span><strong>128</strong></div>
              <div className="stat-row"><span>Contributions</span><strong>1,024</strong></div>
            </div>
          </div>
          {/* LeetCode Mock */}
          <div className="glass-card stat-preview-card leetcode">
            <div className="stat-preview-header">
              <i className="fa-solid fa-code"></i>
              <span>LeetCode</span>
            </div>
            <div className="stat-preview-body">
              <div className="stat-row"><span>Problems Solved</span><strong>350</strong></div>
              <div className="stat-row"><span>Hard</span><strong style={{color: '#ff375f'}}>45</strong></div>
              <div className="stat-row"><span>Medium</span><strong style={{color: '#ffc01e'}}>180</strong></div>
            </div>
          </div>
          {/* Codeforces Mock */}
          <div className="glass-card stat-preview-card codeforces">
            <div className="stat-preview-header">
              <i className="fa-solid fa-chart-simple"></i>
              <span>Codeforces</span>
            </div>
            <div className="stat-preview-body">
              <div className="stat-row"><span>Rating</span><strong>1540</strong></div>
              <div className="stat-row"><span>Max Rating</span><strong>1620</strong></div>
              <div className="stat-row"><span>Rank</span><strong>Specialist</strong></div>
            </div>
          </div>
        </div>
      </section>

      {/* --- EMBED SECTION --- */}
      <section className="embed-section container">
        <div className="embed-container glass-card">
          <div className="embed-text">
            <h2>Embed Your Coding Dashboard Anywhere</h2>
            <p>Whether it's your personal website, portfolio, or a dev blog, you can easily inject your live DevTracker profile using a lightweight iframe.</p>
            <div className="code-snippet">
              <code>
                <span className="code-tag">&lt;iframe</span> <span className="code-attr">src</span>=<span className="code-string">"https://devtracker.app/embed/DEV-XXXXXX"</span> <span className="code-attr">width</span>=<span className="code-string">"100%"</span> <span className="code-tag">&gt;&lt;/iframe&gt;</span>
              </code>
            </div>
          </div>
          <div className="embed-visual">
            <div className="mini-widget glass-card">
              <div className="widget-header">
                <div className="widget-avatar"></div>
                <div className="widget-info">
                  <div className="widget-name"></div>
                  <div className="widget-sub"></div>
                </div>
              </div>
              <div className="widget-stats">
                <div className="w-stat"></div>
                <div className="w-stat"></div>
                <div className="w-stat"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section className="cta-section container">
        <div className="cta-box glass-card">
          <h2>Build Your Developer Profile Today</h2>
          <p>Join developers worldwide who are tracking their skills across multiple platforms in one seamless dashboard.</p>
          <div className="cta-buttons">
            <Link to="/register" className="btn btn-primary btn-glow">Create Free Profile</Link>
            <Link to="/docs" className="btn btn-secondary">Explore Documentation</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
